export type Language = "en" | "es";

export const translations = {
  en: {
    // Header
    nav: {
      services: "How we work",
      howWeWork: "How we work",
      blog: "Blog",
      benefits: "Benefits",
      testimonials: "Testimonials",
      contact: "Contact",
      about: "About",
      caseStudies: "Case Studies",
      cta: "Let's talk",
    },
    // Theme
    theme: {
      light: "Light mode",
      dark: "Dark mode",
    },
    // Hero Section - Variant A
    heroA: {
      headline: "We fix what slows your business down.",
      subheadline: "Real problems, solved with technology. Only when the return is clear.",
      cta: "Tell us what's slowing you down",
      ctaSecondary: "See how we work"
    },
    // Hero Section - Rotator
    heroRotator: {
      questions: [
        "How many hours a week does your team spend typing data that already exists somewhere else?",
        "How many of your systems hold the same information and never talk to each other?",
        "Which tools are you paying for that nobody on your team actually uses?"
      ],
      h1: "We fix what slows your business down.",
      subheadline: "Real problems, solved with technology. Only when the return is clear."
    },
    // Pain Points
    painPoints: {
      title: "What we do",
      subtitle: "The problems we solve",
      items: [
        {
          title: "Data that doesn't flow on its own.",
          description: "Orders, invoices and records that arrive by email and get typed by hand into Excel or a system. We make them move alone, with validation, so errors and lost hours stop."
        },
        {
          title: "Systems that don't talk to each other.",
          description: "Your ERP, your CRM and your spreadsheets hold the same information and nobody connects them. We connect them."
        },
        {
          title: "Tools you pay for and don't use.",
          description: "Copilot, your inbox, your calendar: the features are there and your team doesn't use them. We set them up and train your people until they do."
        }
      ]
    },
    // Benefits
    benefits: {
      title: "What Changes When You Work With Us",
      subtitle: "Real improvements that impact your bottom line.",
      items: [
        {
          title: "Reclaim Your Time",
          description: "Automate repetitive tasks and focus on what actually grows your business.",
        },
        {
          title: "Connected Operations",
          description: "Systems that work together seamlessly, eliminating data gaps and double entry.",
        },
        {
          title: "Streamlined Workflows",
          description: "Modern processes that save time and reduce frustration for you and your team.",
        },
        {
          title: "Get Found Online",
          description: "SEO and AI Optimization (GEO) to help you rank on Google and ChatGPT.",
        },
        {
          title: "Modern Digital Presence",
          description: "A website and tools that reflect your professionalism and build trust.",
        },
        {
          title: "AI-Confident Team",
          description: "We train you and your team to confidently use AI tools, turning new technology into skills your business owns.",
        },
      ],
    },
    // Services
    services: {
      title: "What we fix",
      subtitle: "Real problems, solved with technology.",
      items: [
        {
          title: "Data typed by hand",
          description:
            "Someone re-keys orders, invoices or forms from one place to another. We find where it happens and make it stop.",
          outcomes: ["Fewer errors", "Hours back every week", "Works with your current tools"],
          href: "/how-we-work",
          linkLabel: "See how we work",
        },
        {
          title: "Systems that don't talk to each other",
          description:
            "Your ERP, CRM and spreadsheets hold the same information and never sync. We connect them so the data lives in one place.",
          outcomes: ["One source of truth", "No double entry", "Nothing gets replaced unless that's the fix"],
          href: "/how-we-work",
          linkLabel: "See how we work",
        },
        {
          title: "Tools you pay for and don't use",
          description:
            "Software that was bought with good intentions and never adopted. We set it up properly or train your team to use it.",
          outcomes: ["Use what you already own", "Training for your team", "A clear return or we stop"],
          href: "/how-we-work",
          linkLabel: "See how we work",
        },
      ],
    },
    // Mid-page CTA
    midCta: {
      headline: "Not sure if it's worth fixing? Let's find out together.",
      subtext: "A short conversation, no commitment.",
      button: "Tell us what's slowing you down"
    },
    // How we work (four steps)
    quickWin: {
      title: "How we work",
      subtitle: "Four steps, one problem at a time.",
      steps: [
        {
          number: "01",
          title: "Listen",
          description: "You tell us what's getting in the way. No commitment, no jargon."
        },
        {
          number: "02",
          title: "Diagnose",
          description: "We define the problem, size the fix and show you the return. If it doesn't pay for itself, we tell you."
        },
        {
          number: "03",
          title: "Build",
          description: "We implement it with the right tool for the job: integrations, automation, AI, or training your team."
        },
        {
          number: "04",
          title: "Keep it running",
          description: "We maintain what we built and spot the next problem before it costs you."
        }
      ]
    },
    // Testimonials
    testimonials: {
      title: "Results",
      subtitle: "Real businesses, real problems solved.",
      items: [
        {
          quote:
            "We'd been running key processes manually — spreadsheets, invoices, checks — and it ate our time and was prone to errors. AI Beacon turned all of that into a clear, simple solution, and through precise questions and genuine listening they understood exactly what we needed. We're already working with them on a long-term plan to keep developing Crux.",
          name: "Luciano Guerson",
          role: "",
          company: "Crux",
        },
        {
          quote:
            "AI Beacon took time to understand our mission and goals from day one. The detailed step-by-step plan for design and implementation was exactly what we needed. I can't stress enough how many compliments I've received on the website—it truly reflects our voice and message.",
          name: "Dr. Andres Splenser, MD, FACE",
          role: "",
          company: "Splenser Endocrinology",
          initials: "AS",
          companyUrl: "https://splenserendo.com/",
        },
        {
          quote:
            "Our previous website had gotten old and didn't reflect the quality of the work we do for industrial clients. We started the new site with AI Beacon and the difference was the process: they quickly understood how a company in our industry works, showed us concrete progress at every step, and the timelines they gave were met. I'd recommend them to any industrial SMB that needs a serious digital presence.",
          name: "Fernando K.",
          role: "Owner",
          company: "IMC — Aplicaciones Industriales Calificadas",
          initials: "FK",
          companyUrl: "https://iemece.com.ar/",
        },
      ],
    },
    // Contact Form
    contact: {
      title: "Tell us what's slowing you down",
      subtitle: "If something at work is costing you time or frustration with technology, tell us. We'll understand it together and, if it's worth fixing, we'll fix it.",
      form: {
        name: "Your Name",
        namePlaceholder: "John Smith",
        company: "Company Name (optional)",
        companyPlaceholder: "Acme Corp",
        email: "Email Address",
        emailPlaceholder: "john@example.com",
        challenge: "Main Challenge",
        challengePlaceholder: "Select your biggest challenge",
        challenges: [
          "Data typed by hand that should flow on its own",
          "Systems that don't talk to each other",
          "Tools we pay for and don't use",
          "Other"
        ],
        submit: "Send",
        submitting: "Sending...",
        trustLine: "We'll respond within 24h. No spam.",
      },
      success: {
        title: "Thank you!",
        message: "We've received your message and will reply within 24 hours to understand the problem together.",
      },
      validation: {
        emailRequired: "Email is required",
        emailInvalid: "Please enter a valid email address",
      },
    },
    // Footer
    footer: {
      rights: "All rights reserved.",
      tagline: "We fix what slows your business down.",
      privacyPolicy: "Privacy Policy",
    },
    // Chatbot
    chatbot: {
      title: "AI Beacon",
      welcome: "Hi. Tell me what's slowing your business down and I'll tell you if it's something we fix.",
      placeholder: "Type your message...",
      suggestedQuestions: [
        "Data someone types by hand",
        "Systems that don't talk to each other",
        "Tools we pay for and don't use",
      ],
    },
  },
  es: {
    // Header
    nav: {
      services: "Cómo trabajamos",
      howWeWork: "Cómo trabajamos",
      blog: "Blog",
      benefits: "Beneficios",
      testimonials: "Testimonios",
      contact: "Contacto",
      about: "Sobre nosotros",
      caseStudies: "Casos",
      cta: "Hablemos",
    },
    // Theme
    theme: {
      light: "Modo claro",
      dark: "Modo oscuro",
    },
    // Hero Section - Variant A
    heroA: {
      headline: "Resolvemos lo que frena tu negocio.",
      subheadline: "Problemas reales, resueltos con tecnología. Solo cuando el retorno es claro.",
      cta: "Cuéntanos qué te está frenando",
      ctaSecondary: "Mira cómo trabajamos"
    },
    // Hero Section - Rotator
    heroRotator: {
      questions: [
        "¿Cuántas horas por semana pasa tu equipo cargando datos que ya existen en otro lado?",
        "¿Cuántos de tus sistemas tienen la misma información y nunca se hablan entre sí?",
        "¿Qué herramientas estás pagando que nadie en tu equipo usa de verdad?"
      ],
      h1: "Resolvemos lo que frena tu negocio.",
      subheadline: "Problemas reales, resueltos con tecnología. Solo cuando el retorno es claro."
    },
    // Pain Points
    painPoints: {
      title: "Qué hacemos",
      subtitle: "Los problemas que resolvemos",
      items: [
        {
          title: "Datos que no fluyen solos.",
          description: "Pedidos, facturas y registros que llegan por email y alguien carga a mano en Excel o en un sistema. Hacemos que se muevan solos, con validación, para que se terminen los errores y las horas perdidas."
        },
        {
          title: "Sistemas que no se hablan.",
          description: "Tu ERP, tu CRM y tus planillas tienen la misma información y nadie los conecta. Nosotros los conectamos."
        },
        {
          title: "Herramientas que pagas y no usas.",
          description: "Copilot, tu correo, tu calendario: las funciones están y tu equipo no las usa. Las configuramos y capacitamos a tu gente hasta que las use."
        }
      ]
    },
    // Benefits
    benefits: {
      title: "Qué Cambia Cuando Trabajas Con Nosotros",
      subtitle: "Mejoras reales que impactan tu resultado final.",
      items: [
        {
          title: "Recupera Tu Tiempo",
          description: "Automatiza tareas repetitivas y enfócate en lo que realmente hace crecer tu negocio.",
        },
        {
          title: "Operaciones Conectadas",
          description: "Sistemas que funcionan juntos sin problemas, eliminando brechas de datos y doble entrada.",
        },
        {
          title: "Flujos de Trabajo Optimizados",
          description: "Procesos modernos que ahorran tiempo y reducen la frustración para ti y tu equipo.",
        },
        {
          title: "Que Te Encuentren Online",
          description: "SEO y Optimización para IA (GEO) para ayudarte a posicionarte en Google y ChatGPT.",
        },
        {
          title: "Presencia Digital Moderna",
          description: "Un sitio web y herramientas que reflejan tu profesionalismo y construyen confianza.",
        },
        {
          title: "Equipo Confiado en IA",
          description: "Capacitamos a ti y tu equipo para usar herramientas de IA con confianza, convirtiendo nueva tecnología en habilidades que tu negocio domina.",
        },
      ],
    },
    // Services
    services: {
      title: "Lo que resolvemos",
      subtitle: "Problemas reales, resueltos con tecnología.",
      items: [
        {
          title: "Datos cargados a mano",
          description:
            "Alguien vuelve a cargar pedidos, facturas o formularios de un lugar a otro. Encontramos dónde pasa y lo hacemos parar.",
          outcomes: ["Menos errores", "Horas recuperadas cada semana", "Funciona con tus herramientas actuales"],
          href: "/es/como-trabajamos",
          linkLabel: "Mira cómo trabajamos",
        },
        {
          title: "Sistemas que no se hablan entre sí",
          description:
            "Tu ERP, tu CRM y tus planillas tienen la misma información y nunca se sincronizan. Los conectamos para que los datos vivan en un solo lugar.",
          outcomes: ["Una sola fuente de verdad", "Sin doble carga", "No se reemplaza nada salvo que sea la solución"],
          href: "/es/como-trabajamos",
          linkLabel: "Mira cómo trabajamos",
        },
        {
          title: "Herramientas que pagas y no usas",
          description:
            "Software que se compró con buenas intenciones y nunca se adoptó. Lo configuramos bien o capacitamos a tu equipo para usarlo.",
          outcomes: ["Usa lo que ya tienes", "Capacitación para tu equipo", "Un retorno claro o paramos"],
          href: "/es/como-trabajamos",
          linkLabel: "Mira cómo trabajamos",
        },
      ],
    },
    // Mid-page CTA
    midCta: {
      headline: "¿No sabes si vale la pena resolverlo? Lo vemos juntos.",
      subtext: "Una conversación corta, sin compromiso.",
      button: "Cuéntanos qué te está frenando"
    },
    // How we work (four steps)
    quickWin: {
      title: "Cómo trabajamos",
      subtitle: "Cuatro pasos, un problema a la vez.",
      steps: [
        {
          number: "01",
          title: "Escuchar",
          description: "Nos cuentas qué te está trabando. Sin compromiso, sin jerga."
        },
        {
          number: "02",
          title: "Diagnosticar",
          description: "Definimos el problema, dimensionamos la solución y te mostramos el retorno. Si no se paga sola, te lo decimos."
        },
        {
          number: "03",
          title: "Construir",
          description: "Lo implementamos con la herramienta que corresponde: integraciones, automatización, IA, o capacitación de tu equipo."
        },
        {
          number: "04",
          title: "Mantener",
          description: "Mantenemos lo que construimos y detectamos el próximo problema antes de que te cueste."
        }
      ]
    },
    // Testimonials
    testimonials: {
      title: "Resultados",
      subtitle: "Empresas reales, problemas resueltos.",
      items: [
        {
          quote:
            "Veníamos manejando procesos clave de forma muy manual — planillas, facturas, cheques — y eso nos consumía tiempo y era propenso a errores. AI Beacon transformó todo eso en una solución clara y simple, y con preguntas precisas y una escucha genuina entendieron exactamente lo que necesitábamos. Ya estamos trabajando con ellos en un plan a largo plazo para seguir desarrollando Crux.",
          name: "Luciano Guerson",
          role: "",
          company: "Crux",
        },
        {
          quote:
            "AI Beacon se tomó el tiempo de entender nuestra misión y objetivos desde el primer día. El plan detallado paso a paso para diseño e implementación fue exactamente lo que necesitábamos. No puedo expresar cuántos cumplidos he recibido sobre el sitio web—realmente refleja nuestra voz y mensaje.",
          name: "Dr. Andres Splenser, MD, FACE",
          role: "",
          company: "Splenser Endocrinology",
          initials: "AS",
          companyUrl: "https://splenserendo.com/",
        },
        {
          quote:
            "Nuestra web anterior había quedado vieja y no reflejaba el nivel de las obras que hacemos para clientes industriales. Con AI Beacon arrancamos el sitio nuevo y la diferencia estuvo en el proceso: entendieron rápido cómo trabaja una empresa de nuestro rubro, nos mostraron avances concretos en cada instancia y los tiempos que dieron se cumplieron. A cualquier PyME industrial que necesite una presencia digital seria, se los recomiendo.",
          name: "Fernando K.",
          role: "Propietario",
          company: "IMC — Aplicaciones Industriales Calificadas",
          initials: "FK",
          companyUrl: "https://iemece.com.ar/",
        },
      ],
    },
    // Contact Form
    contact: {
      title: "Cuéntanos qué te está frenando",
      subtitle: "Si hay algo en tu trabajo que te hace perder tiempo o te frustra con la tecnología, cuéntanos. Lo entendemos juntos y, si vale la pena, lo resolvemos.",
      form: {
        name: "Tu Nombre",
        namePlaceholder: "Juan García",
        company: "Nombre de la Empresa (opcional)",
        companyPlaceholder: "Mi Empresa S.A.",
        email: "Correo Electrónico",
        emailPlaceholder: "juan@ejemplo.com",
        challenge: "Principal Desafío",
        challengePlaceholder: "Selecciona tu mayor desafío",
        challenges: [
          "Datos cargados a mano que deberían fluir solos",
          "Sistemas que no se hablan entre sí",
          "Herramientas que pagamos y no usamos",
          "Otro"
        ],
        submit: "Enviar",
        submitting: "Enviando...",
        trustLine: "Responderemos en 24h. Sin spam.",
      },
      success: {
        title: "¡Gracias!",
        message: "Recibimos tu mensaje y te responderemos en 24 horas para entender el problema juntos.",
      },
      validation: {
        emailRequired: "El correo electrónico es obligatorio",
        emailInvalid: "Por favor ingresa un correo electrónico válido",
      },
    },
    // Footer
    footer: {
      rights: "Todos los derechos reservados.",
      tagline: "Resolvemos lo que frena tu negocio.",
      privacyPolicy: "Política de Privacidad",
    },
    // Chatbot
    chatbot: {
      title: "AI Beacon",
      welcome: "Hola. Cuéntame qué está frenando tu negocio y te digo si es algo que resolvemos.",
      placeholder: "Escribe tu mensaje...",
      suggestedQuestions: [
        "Datos que alguien carga a mano",
        "Sistemas que no se hablan entre sí",
        "Herramientas que pagamos y no usamos",
      ],
    },
  },
};

export function getTranslation(lang: Language) {
  return translations[lang];
}
